'use client';

import { useMemo } from 'react';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';

export const FILE_READ_AS = {
  Text: 'text',
  ArrayBuffer: 'arrayBuffer',
  DataUrl: 'dataUrl',
} as const;
export type FileReadAs = typeof FILE_READ_AS[keyof typeof FILE_READ_AS];

export interface FileReadVars {
  file: File;
  as: FileReadAs;
}

const FILE_READ_ABORT_MESSAGE = 'File read aborted.';
const FILE_READ_ABORT_ERROR_NAME = 'AbortError';
const FILE_READ_FAILED_MESSAGE = 'File read failed.';

function readDataUrl(file: File, signal?: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const abort = () => {
      reader.abort();
      reject(new DOMException(FILE_READ_ABORT_MESSAGE, FILE_READ_ABORT_ERROR_NAME));
    };
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error(FILE_READ_FAILED_MESSAGE));
    signal?.addEventListener('abort', abort, { once: true });
    reader.onloadend = () => signal?.removeEventListener('abort', abort);
    reader.readAsDataURL(file);
  });
}

async function readFile({ file, as }: FileReadVars, ctx?: { signal: AbortSignal }) {
  if (ctx?.signal.aborted) throw new DOMException(FILE_READ_ABORT_MESSAGE, FILE_READ_ABORT_ERROR_NAME);
  if (as === FILE_READ_AS.Text) return file.text();
  if (as === FILE_READ_AS.ArrayBuffer) return file.arrayBuffer();
  return readDataUrl(file, ctx?.signal);
}

export function useFileRead(): ActionResult<FileReadVars, string | ArrayBuffer> {
  return useActionHook<FileReadVars, string | ArrayBuffer>(
    useMemo(
      () => ({ mutationFn: readFile, abortable: true }),
      [],
    ),
  );
}
